import { AvailableAdjustmentDto } from './available-adjustment-operations';
import { CoPilotPositionDto } from './copilot-operations';
import { Leg, Solution } from './copilot-domain';
import { IHedgingGridCopilotFacade } from './copilot-facade';

export interface OpportunityLeg {
    ticker: string; underlying: string; optionRoot: string; optionRight: string;
    expiration: string; strike: number; quantity: number; mid: number | null;
}
export interface AdjustmentProposal {
    schemaVersion: number; assessmentId: string; ownerId: string; portfolioId: string;
    sourceKey: string; groupId: string; holdingsFingerprint: string; recipe: string;
    hedgeLabel: string; underlying: string; optionRight: string; expiration: string;
    availability: string; health: string; reason: string; spot: number | null;
    lowerBand: number | null; upperBand: number | null;
    netCredit: number | null; orderCredit: number | null; ledgerCredit: number | null;
    defaultQty: number; before: OpportunityLeg[]; transaction: OpportunityLeg[]; after: OpportunityLeg[];
}
export interface AdjustmentCard {
    itemId: string; proposal: AdjustmentProposal; solution: Solution; held: CoPilotPositionDto[];
    busy: boolean; message: string; currentlyAvailable: boolean;
}
const finite = (n: any): n is number => typeof n === 'number' && isFinite(n);
export function canPreview(p: AdjustmentProposal): boolean {
    return !!p && p.health === 'confirmed' && p.availability === 'available';
}

// Reject unknown versions and malformed contracts at the boundary. Never guess an after-state.
export function readProposal(dto: AvailableAdjustmentDto, owner: string, portfolio: string): AdjustmentProposal {
    const p: AdjustmentProposal = JSON.parse(dto.proposalJson);
    if (!dto.actionItemId || !p || p.schemaVersion !== 1 || p.recipe !== 'butterfly-to-vertical-v1'
        || p.ownerId.toLowerCase() !== owner.toLowerCase() || p.portfolioId !== portfolio
        || !p.assessmentId || !p.groupId || !p.holdingsFingerprint || !p.sourceKey
        || !['before', 'transaction', 'after'].every(key => Array.isArray(p[key]) && p[key].every((l: OpportunityLeg) =>
            l && typeof l.ticker === 'string' && finite(l.quantity) && Number.isSafeInteger(l.quantity) && l.quantity !== 0
            && finite(l.strike) && !!l.underlying && !!l.optionRoot && /^\d{4}-\d{2}-\d{2}$/.test(l.expiration)
            && (l.optionRight === 'Call' || l.optionRight === 'Put')
            && contractKey(l.ticker) === contractKey(`${l.optionRoot} ${l.underlying} ${l.optionRight} ${l.strike} ${l.expiration}`)))) {
        throw new Error('The Adjustment Is Not Compatible With This ORION Panel.');
    }
    if (canPreview(p) && (p.before.length !== 3 || p.after.length !== 2 || !p.transaction.length)) {
        throw new Error('The Adjustment Has Incomplete Holdings.');
    }
    return p;
}
export function contractKey(ticker: string): string {
    const parts = (ticker || '').trim().split(/\s+/);
    return parts.length === 5 && finite(Number(parts[3]))
        ? [parts[0], parts[1], parts[2], Number(parts[3]), parts[4]].join('|') : '';
}
function holdingsKey(legs: {ticker: string, quantity: number}[]): string {
    const net = new Map<string, number>();
    for (const l of legs) {
        const key = contractKey(l.ticker);
        if (!key) { return 'invalid'; }
        net.set(key, (net.get(key) || 0) + l.quantity);
    }
    return JSON.stringify(Array.from(net).filter(x => x[1] !== 0).sort((a, b) => a[0].localeCompare(b[0])));
}
export function placementRefusal(p: AdjustmentProposal, ctx: IHedgingGridCopilotFacade): string | null {
    if (!canPreview(p)) { return 'This Adjustment Is No Longer Available.'; }
    if (!ctx || !ctx.isEntitled()) { return 'ORION Is Not Available in This Panel.'; }
    if (ctx.isCopilotHedgeModified(p.groupId)) { return 'The Hedge Has Manual Changes. Keep or Revert Them First.'; }
    const hedge = (ctx.readBookHedges() || []).find(h => h.id === p.groupId);
    if (hedge?.previewed) { return 'Another Preview Already Uses This Hedge.'; }
    const held = (ctx.readWorkingStatePositions() || []).filter(l => l.groupId === p.groupId);
    if (holdingsKey(held) !== holdingsKey(p.before)) { return 'The Loaded Hedge Differs From the Saved Adjustment. Reload the Book.'; }
    const base = ctx.readBaseAsset();
    if (p.underlying !== base && !ctx.readMatrixEsHedgingEnabled()) { return 'Enable ES Hedging to Preview This Existing Hedge.'; }
    const chain = (p.underlying === base ? ctx.readAvailableExpirationDescriptors() : ctx.readEsExpirationDescriptors(p.underlying)) || [];
    if (!p.after.every(l => chain.some(e => e && e.optionExpirationDate === l.expiration && e.expirationTicker === l.optionRoot
        && e.underlyingTicker === l.underlying && Array.isArray(e.strikes) && e.strikes.indexOf(l.strike) >= 0))) {
        return 'The Exact Option Series and Strikes Are Not Loaded in the Matrix.';
    }
    return null;
}
export function buildCard(itemId: string, p: AdjustmentProposal, solutionId: string): AdjustmentCard {
    const legs: Leg[] = p.transaction.map(l => {
        const held = p.before.find(b => contractKey(b.ticker) === contractKey(l.ticker));
        const after = p.after.find(a => contractKey(a.ticker) === contractKey(l.ticker));
        const effect = !held ? 'open' : !after ? 'close' : held.quantity * after.quantity < 0 ? 'reverse'
            : Math.abs(after.quantity) > Math.abs(held.quantity) ? 'increase' : 'decrease';
        const side = l.quantity > 0 ? 'buy' : 'sell';
        const action = (side === 'buy' ? 'Buy' : 'Sell') + ' To ' + effect[0].toUpperCase() + effect.slice(1);
        return {quantity: Math.abs(l.quantity), ticker: l.ticker, tickerDisplayName: '', action, side, effect,
            asset: l.underlying, groupId: p.groupId, optionType: l.optionRight, strike: l.strike,
            expiry: l.expiration, hedgeLabel: p.hedgeLabel};
    });
    const title = `${p.hedgeLabel || p.underlying} · Butterfly to Vertical`;
    return {itemId, proposal: p, busy: false, message: '', currentlyAvailable: canPreview(p), solution: {id: solutionId,
        content: {skillId: p.recipe, skillUniqueId: p.assessmentId, displayName: title,
            shortDescription: `${p.optionRight} Butterfly to Vertical`,
            extendedDescription: `Available Between ${p.lowerBand} and ${p.upperBand}, Including Both Edges. Preview Loads and Prices the Transaction in the Hedge Matrix.`,
            legs, cards: [{title: `${p.underlying} ${p.optionRight} Vertical`, legs,
                targetHedgeId: p.groupId, targetOutcome: p.after.map(l => ({quantity: l.quantity, ticker: l.ticker, tickerDisplayName: ''}))}]},
        state: {previewed: false, applied: false, blocked: false}},
        held: p.before.map(l => ({ticker: l.ticker, underlying: l.underlying, asset: l.underlying, quantity: l.quantity,
            strike: l.strike, expiry: l.expiration, optionType: l.optionRight, avgPrice: null,
            label: p.hedgeLabel, groupId: p.groupId}))};
}
